import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, MessageCircle, Send, Trophy, Award, Star, Calendar, Search, ThumbsUp } from 'lucide-react';
import { crmAPI, bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { format, parseISO, isToday, isFuture } from 'date-fns';

export default function CommunityPage() {
  const { user, studio } = useAuth();
  const [activeTab, setActiveTab] = useState('discussions'); // 'discussions', 'leaderboard', 'find-classmates'
  const [newPost, setNewPost] = useState('');
  const [searchClass, setSearchClass] = useState('');

  const studioId = studio?.id || user?.studioId;

  // Fetch all customers for the studio
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers', studioId],
    queryFn: () => crmAPI.getCustomers(studioId),
    enabled: !!studioId,
    retry: false
  });

  const customers = Array.isArray(customersData?.data) ? customersData.data : [];

  // Fetch bookings for all customers to calculate leaderboard
  const { data: allBookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['all-bookings', studioId],
    queryFn: async () => {
      // Fetch bookings for each customer
      const bookingsPromises = customers.map(customer =>
        bookingsAPI.getMyBookings(customer.id).catch(() => ({ data: [] }))
      );
      const results = await Promise.all(bookingsPromises);
      return results.flatMap(r => r.data || []);
    },
    enabled: !!studioId && customers.length > 0,
    retry: false
  });

  const allBookings = Array.isArray(allBookingsData) ? allBookingsData : [];

  // Calculate leaderboard from bookings
  const leaderboardData = customers
    .map(customer => {
      const customerBookings = allBookings.filter(b => b.studentId === customer.id);
      const completedClasses = customerBookings.filter(b => {
        if (!b.schedule?.specificDate) return false;
        const date = parseISO(b.schedule.specificDate);
        return date < new Date();
      }).length;
      
      return {
        id: customer.id,
        name: `${customer.firstName} ${customer.lastName}`,
        email: customer.email,
        classes: completedClasses,
        avatar: null
      };
    })
    .filter(member => member.classes > 0)
    .sort((a, b) => b.classes - a.classes)
    .map((item, index) => ({ ...item, rank: index + 1 }));

  // Placeholder discussions - in real app, this would come from API
  const [discussions, setDiscussions] = useState([
    {
      id: 1,
      author: 'Sarah Johnson',
      authorId: '1',
      content: 'Just finished an amazing yoga session! Who else is going to the evening class tomorrow?',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      likes: 12,
      replies: 5,
      classMention: 'Yoga Evening Class'
    },
    {
      id: 2,
      author: 'Mike Chen',
      authorId: '2',
      content: 'Looking for a workout buddy for the fitness class this weekend. Anyone interested?',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
      likes: 8,
      replies: 3,
      classMention: 'Fitness Weekend'
    },
    {
      id: 3,
      author: 'Emma Williams',
      authorId: '3',
      content: 'The meditation class was so relaxing! Highly recommend it to everyone.',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      likes: 15,
      replies: 7,
      classMention: 'Meditation Class'
    }
  ]);

  const handlePostDiscussion = () => {
    if (!newPost.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    const newDiscussion = {
      id: discussions.length + 1,
      author: `${user?.firstName} ${user?.lastName}`,
      authorId: user?.id,
      content: newPost,
      timestamp: new Date(),
      likes: 0,
      replies: 0,
      classMention: null,
      isStudio: true
    };
    
    setDiscussions([newDiscussion, ...discussions]);
    setNewPost('');
    toast.success('Post shared!');
  };

  const handleLike = (id) => {
    setDiscussions(discussions.map(d => 
      d.id === id ? { ...d, likes: d.likes + 1 } : d
    ));
  };

  // Get upcoming classes from all bookings
  const upcomingClasses = allBookings
    .filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const date = parseISO(booking.schedule.specificDate);
      return isFuture(date) || isToday(date);
    })
    .reduce((acc, booking) => {
      const scheduleId = booking.scheduleId;
      const existing = acc.find(c => c.scheduleId === scheduleId);
      const className = booking.schedule?.video?.title || 'Class';
      const classDate = parseISO(booking.schedule.specificDate);
      
      if (existing) {
        existing.students.push({
          id: booking.studentId,
          name: booking.student?.firstName && booking.student?.lastName
            ? `${booking.student.firstName} ${booking.student.lastName}`
            : 'Student'
        });
      } else {
        acc.push({
          scheduleId,
          className,
          classDate,
          students: [{
            id: booking.studentId,
            name: booking.student?.firstName && booking.student?.lastName
              ? `${booking.student.firstName} ${booking.student.lastName}`
              : 'Student'
          }]
        });
      }
      return acc;
    }, [])
    .sort((a, b) => a.classDate - b.classDate);

  // Get rank badge/icon
  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: Trophy, color: 'text-yellow-500', bgStyle: { background: 'var(--highlight-10)' }, label: '🥇 Champion' };
    if (rank === 2) return { icon: Award, color: 'text-gray-400', bgStyle: { background: 'var(--highlight-10)' }, label: '🥈 Runner-up' };
    if (rank === 3) return { icon: Award, color: 'text-orange-500', bgStyle: { background: 'var(--highlight-10)' }, label: '🥉 Third Place' };
    if (rank <= 10) return { icon: Star, color: 'text-purple-500', bgStyle: { background: 'var(--highlight-10)' }, label: '⭐ Top 10' };
    if (rank <= 25) return { icon: Star, color: 'text-blue-500', bgStyle: { background: 'var(--highlight-10)' }, label: '⭐ Top 25' };
    return { icon: null, color: 'text-[var(--text-secondary)]', bgStyle: { background: 'var(--highlight-10)' }, label: `#${rank}` };
  };

  // Get prize for rank
  const getPrize = (rank, classes) => {
    if (rank === 1 && classes >= 50) return '🏆 Grand Champion - Free 3-month subscription + Studio merchandise!';
    if (rank === 1) return '🏆 Champion - Free 1-month subscription!';
    if (rank === 2) return '🥈 Runner-up - 50% off next month!';
    if (rank === 3) return '🥉 Third Place - 25% off next month!';
    if (rank <= 10 && classes >= 30) return '⭐ Top 10 - 15% off next month!';
    if (classes >= 25) return '🎁 Milestone - 10% off next class!';
    if (classes >= 10) return '🎉 Achievement - Keep it up!';
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-primary)]">Community</h1>
        <p className="text-[var(--text-secondary)] mt-2">Engage with your students, view discussions, and track the leaderboard!</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--border-subtle)]">
        <button
          onClick={() => setActiveTab('discussions')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'discussions'
              ? 'text-[var(--text-secondary)] border-b-2 border-[var(--primary)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Discussions
        </button>
        <button
          onClick={() => setActiveTab('leaderboard')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'leaderboard'
              ? 'text-[var(--text-secondary)] border-b-2 border-[var(--primary)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <Trophy className="w-4 h-4" />
          Leaderboard
        </button>
        <button
          onClick={() => setActiveTab('find-classmates')}
          className={`px-4 py-2 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'find-classmates'
              ? 'text-[var(--text-secondary)] border-b-2 border-[var(--primary)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          <Users className="w-4 h-4" />
          Class Attendance
        </button>
      </div>

      {/* Discussions Tab */}
      {activeTab === 'discussions' && (
        <div className="space-y-6">
          {/* Create Post */}
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-primary-200">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Share a Message with Your Students</h3>
            <div className="space-y-3">
              <textarea
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="Share announcements, encouragement, or respond to student discussions..."
                className="w-full p-3 border border-[var(--border-subtle)] rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
                rows={4}
              />
              <button
                onClick={handlePostDiscussion}
                className="btn btn-primary flex items-center gap-2 ml-auto shadow-md hover:shadow-lg transition-all duration-300"
              >
                <Send className="w-4 h-4" />
                Post
              </button>
            </div>
          </div>

          {/* Discussions Feed */}
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-[var(--text-primary)]">Community Discussions</h3>
            {discussions.map((discussion) => (
              <div key={discussion.id} className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-primary-200">
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${discussion.isStudio ? 'bg-primary-600' : ''}`}
                    style={!discussion.isStudio ? { background: 'var(--highlight-10)' } : {}}
                  >
                    <Users className={`w-6 h-6 ${discussion.isStudio ? 'text-white' : 'text-[var(--text-secondary)]'}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-[var(--text-primary)]">
                        {discussion.author}
                        {discussion.isStudio && <span className="text-xs bg-primary-600 text-white px-2 py-0.5 rounded ml-2">Studio</span>}
                      </h4>
                      <span className="text-xs text-[var(--text-muted)]">
                        {format(discussion.timestamp, 'MMM d, h:mm a')}
                      </span>
                      {discussion.classMention && (
                        <span className="text-xs text-[var(--text-secondary)] px-2 py-1 rounded" style={{ background: 'var(--highlight-5)', border: '1px solid var(--border-subtle)' }}>
                          {discussion.classMention}
                        </span>
                      )}
                    </div>
                    <p className="text-[var(--text-secondary)] mb-3">{discussion.content}</p>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleLike(discussion.id)}
                        className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-secondary)] transition-colors"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        <span>{discussion.likes}</span>
                      </button>
                      <button className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-secondary)] transition-colors">
                        <MessageCircle className="w-4 h-4" />
                        <span>{discussion.replies} replies</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div className="space-y-6">
          {/* Leaderboard */}
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-primary-200">
            <h3 className="text-xl font-bold text-[var(--text-primary)] mb-4">Top Performers</h3>
            {leaderboardData.length === 0 ? (
              <div className="text-center py-12">
                <Trophy className="w-16 h-16 text-primary-300 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">No Data Yet</h3>
                <p className="text-[var(--text-secondary)]">Students will appear here once they complete classes!</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {leaderboardData.slice(0, 10).map((member) => {
                    const badge = getRankBadge(member.rank);
                    const BadgeIcon = badge.icon;
                    const prize = getPrize(member.rank, member.classes);
                    
                    return (
                      <div
                        key={member.id}
                        className="p-4 rounded-lg border-2 transition-all duration-300 transform hover:-translate-y-0.5 border-[var(--border-subtle)] hover:shadow-lg hover:border-primary-200"
                        style={{ background: 'var(--bg-elevated)' }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-12 h-12">
                              {BadgeIcon ? (
                                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={badge.bgStyle}>
                                  <BadgeIcon className={`w-6 h-6 ${badge.color}`} />
                                </div>
                              ) : (
                                <span className={`text-lg font-bold ${badge.color}`}>#{member.rank}</span>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-[var(--text-primary)]">{member.name}</h4>
                              </div>
                              <p className="text-sm text-[var(--text-secondary)]">{member.classes} classes completed</p>
                              <p className="text-xs text-[var(--text-muted)]">{member.email}</p>
                            </div>
                          </div>
                          {prize && (
                            <div className="text-right">
                              <p className="text-sm font-semibold text-[var(--text-secondary)]">{prize}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Prize Information */}
                <div className="mt-6 pt-6 border-t border-[var(--border-subtle)]">
                  <h4 className="font-semibold text-[var(--text-primary)] mb-3">🏆 Prize Tiers</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="p-3 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <p className="font-semibold text-yellow-500">🥇 #1 (50+ classes)</p>
                      <p className="text-[var(--text-secondary)]">Free 3-month subscription + Studio merchandise!</p>
                    </div>
                    <div className="p-3 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <p className="font-semibold text-[var(--text-secondary)]">🥈 #2</p>
                      <p className="text-[var(--text-secondary)]">50% off next month!</p>
                    </div>
                    <div className="p-3 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <p className="font-semibold text-orange-500">🥉 #3</p>
                      <p className="text-[var(--text-secondary)]">25% off next month!</p>
                    </div>
                    <div className="p-3 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <p className="font-semibold text-purple-500">⭐ Top 10 (30+ classes)</p>
                      <p className="text-[var(--text-secondary)]">15% off next month!</p>
                    </div>
                    <div className="p-3 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <p className="font-semibold text-blue-500">🎁 25+ Classes</p>
                      <p className="text-[var(--text-secondary)]">10% off next class!</p>
                    </div>
                    <div className="p-3 rounded-lg border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                      <p className="font-semibold text-green-500">🎉 10+ Classes</p>
                      <p className="text-[var(--text-secondary)]">Achievement unlocked!</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Find Classmates Tab */}
      {activeTab === 'find-classmates' && (
        <div className="space-y-6">
          <div className="card hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-primary-200">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Upcoming Classes & Attendance</h3>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--text-muted)]" />
              <input
                type="text"
                value={searchClass}
                onChange={(e) => setSearchClass(e.target.value)}
                placeholder="Search for a class..."
                className="w-full pl-10 pr-4 py-2 border border-[var(--border-subtle)] rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}
              />
            </div>
          </div>

          {upcomingClasses.length === 0 ? (
            <div className="card text-center py-12">
              <Calendar className="w-16 h-16 text-primary-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">No Upcoming Classes</h3>
              <p className="text-[var(--text-secondary)]">Students will appear here once they book classes!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingClasses
                .filter(classItem => {
                  if (!searchClass) return true;
                  return classItem.className.toLowerCase().includes(searchClass.toLowerCase());
                })
                .map((classItem) => (
                  <div key={classItem.scheduleId} className="card group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-transparent hover:border-primary-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-[var(--text-primary)] mb-2 group-hover:text-[var(--text-secondary)] transition-colors">{classItem.className}</h4>
                        <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)] mb-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{format(classItem.classDate, 'EEEE, MMMM d, yyyy')}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            <span>{classItem.students.length} {classItem.students.length === 1 ? 'student' : 'students'} registered</span>
                          </div>
                        </div>
                        {/* Show students registered for this class */}
                        <div className="p-3 rounded-lg" style={{ background: 'var(--highlight-5)' }}>
                          <p className="text-sm text-[var(--text-secondary)] mb-2">
                            <strong>Registered Students:</strong>
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {classItem.students.map((student) => (
                              <span key={student.id} className="text-xs text-[var(--text-secondary)] px-2 py-1 rounded border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
                                {student.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

